/**
 * Manage, Campaigns: one send (plan docs/plans/lms-plan.md 8.2 "a per-person
 * outcome table", 5.4, 18.16, 18.21; WP-43; R6, R8, G9, G14). Route
 * /lms/manage/campaigns/:campaignId.
 *
 * GET /admin/campaigns/:id: the campaign (course, kind, who sent it and when,
 * due date, message, force, a pinned version, the audience as selected) and
 * one row per person the viewing admin reaches with the outcome of the send
 * and where their assignment stands now (open, overdue, completed, waived,
 * cancelled). People named in the audience whom the admin does not reach are
 * counted, never named (audience_hidden_user_count, 18.21). Site names come
 * from the sites the admin may switch to; group names from GET /admin/groups
 * (a failure there leaves a count instead of names).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Megaphone } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import { useAuth } from '../../../../contexts/AuthContext';
import { apiErrorMessage } from '../../../api/client';
import { CAMPAIGN_OUTCOMES, getCampaign } from '../../../api/campaigns';
import type { CampaignDetail, CampaignOutcome } from '../../../api/campaigns';
import { listGroups } from '../../../api/groups';
import {
  cardCls,
  ErrorNote,
  formatDay,
  linkBtnCls,
  metaCls,
  microLabelCls,
  OUTCOME_LABELS,
  OutcomePill,
  Pill,
  personName,
  rowCls,
  sectionTitleCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
} from '../../../components/pickers/shared';
import { assignmentState, audienceSummary, CAMPAIGNS_BASE, CampaignKindPill } from './labels';

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className={microLabelCls}>{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  );
}

export function CampaignDetailPage() {
  const { campaignId = '' } = useParams();
  const { availableSites } = useAuth();
  const [detail, setDetail] = useState<CampaignDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; notFound: boolean } | null>(null);
  const [groupNames, setGroupNames] = useState<Record<string, string>>({});
  const [reloadKey, setReloadKey] = useState(0);
  const request = useRef(0);

  useEffect(() => {
    const id = ++request.current;
    setLoading(true);
    getCampaign(campaignId)
      .then((d) => {
        if (id !== request.current) return;
        setDetail(d);
        setError(null);
      })
      .catch((err) => {
        if (id !== request.current) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        setError({ message: apiErrorMessage(err, 'Could not load the campaign.'), notFound: status === 404 });
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
  }, [campaignId, reloadKey]);

  // Group names for the audience line; only asked when the audience names groups.
  const groupIds = detail?.campaign.audience?.group_ids ?? [];
  const wantsGroups = groupIds.length > 0;
  useEffect(() => {
    if (!wantsGroups) return undefined;
    let cancelled = false;
    listGroups({ includeInactive: true, limit: 200 })
      .then((page) => {
        if (cancelled) return;
        const names: Record<string, string> = {};
        for (const g of page.items ?? []) names[g.id] = g.name;
        setGroupNames(names);
      })
      .catch(() => {
        // The audience line falls back to a count (see the header).
      });
    return () => {
      cancelled = true;
    };
  }, [wantsGroups]);

  const siteNames = useMemo(() => {
    const out: Record<string, string> = {};
    for (const s of availableSites ?? []) out[s.id] = s.name;
    return out;
  }, [availableSites]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);
  const nowMs = Date.now();

  if (loading && !detail) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        Loading the campaign
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="space-y-3">
        <Link to={CAMPAIGNS_BASE} className={linkBtnCls}>
          <ArrowLeft className="h-4 w-4" />
          All campaigns
        </Link>
        {error.notFound ? (
          <EmptyState icon={Megaphone} variant="card" title="Campaign not found" hint="It may belong to another network, or the link is wrong." />
        ) : (
          <ErrorNote>
            {error.message}{' '}
            <button type="button" className="font-medium underline" onClick={reload}>
              Retry
            </button>
          </ErrorNote>
        )}
      </div>
    );
  }

  if (!detail) return null;
  const { campaign, recipients } = detail;
  const tally = CAMPAIGN_OUTCOMES.reduce((acc, o) => ({ ...acc, [o]: 0 }), {} as Record<CampaignOutcome, number>);
  for (const r of recipients) tally[r.outcome] = (tally[r.outcome] ?? 0) + 1;
  const audience = audienceSummary(campaign, siteNames, groupNames);
  const hiddenRecipients = Math.max(0, (campaign.recipient_count ?? 0) - recipients.length);

  return (
    <div className="space-y-4" data-testid="campaign-detail">
      <Link to={CAMPAIGNS_BASE} className={linkBtnCls}>
        <ArrowLeft className="h-4 w-4" />
        All campaigns
      </Link>

      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-testid="campaign-course">
          {campaign.course?.title ?? 'A course no longer listed'}
        </h2>
        <CampaignKindPill kind={campaign.kind} />
        {campaign.force && <Pill tone="amber">Forced</Pill>}
      </div>

      <dl className={`${cardCls} grid gap-4 sm:grid-cols-2 xl:grid-cols-4`}>
        <Fact label="Sent">
          {formatDay(campaign.sent_at)}
          {campaign.sent_by_name ? ` by ${campaign.sent_by_name}` : ''}
        </Fact>
        <Fact label="Due">
          {formatDay(campaign.due_at)} <span className={metaCls}>{`(${campaign.due_days} ${campaign.due_days === 1 ? 'day' : 'days'})`}</span>
        </Fact>
        <Fact label="Version">{campaign.course_version_id ? 'Pinned to the version current when sent' : 'The current version when each person starts'}</Fact>
        <Fact label="People reached">{(campaign.recipient_count ?? 0).toLocaleString()}</Fact>
        <div className="sm:col-span-2 xl:col-span-4">
          <dt className={microLabelCls}>Audience</dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100" data-testid="campaign-audience">
            {audience.length > 0 ? audience.join('; ') : <span className={metaCls}>Not recorded</span>}
          </dd>
        </div>
        {campaign.message && (
          <div className="sm:col-span-2 xl:col-span-4">
            <dt className={microLabelCls}>Message</dt>
            <dd className="mt-0.5 whitespace-pre-line text-sm text-gray-900 dark:text-gray-100">{campaign.message}</dd>
          </div>
        )}
      </dl>

      <section aria-labelledby="lms-campaign-outcomes" className="space-y-2">
        <h3 id="lms-campaign-outcomes" className={sectionTitleCls}>
          Who received it
        </h3>
        <p className={metaCls} data-testid="campaign-tally">
          {CAMPAIGN_OUTCOMES.map((o) => `${OUTCOME_LABELS[o]}: ${tally[o].toLocaleString()}`).join(', ')}
        </p>
        {hiddenRecipients > 0 && (
          <p className={metaCls} data-testid="campaign-hidden-recipients">
            {`${hiddenRecipients.toLocaleString()} more ${hiddenRecipients === 1 ? 'person is' : 'people are'} at sites you do not reach; counted, not shown.`}
          </p>
        )}
        {recipients.length === 0 ? (
          <div className={`${cardCls} text-sm text-gray-500 dark:text-gray-400`}>Nobody you can see received this send.</div>
        ) : (
          <div className={tableCardCls}>
            <table className={tableCls}>
              <thead className={theadCls}>
                <tr>
                  <th scope="col" className={thCls}>
                    Person
                  </th>
                  <th scope="col" className={thCls}>
                    Outcome of the send
                  </th>
                  <th scope="col" className={thCls}>
                    Assignment now
                  </th>
                </tr>
              </thead>
              <tbody className={tbodyCls}>
                {recipients.map((r) => {
                  const state = assignmentState(r.assignment, nowMs);
                  return (
                    <tr key={r.user_id} className={rowCls} data-testid={`recipient-row-${r.user_id}`}>
                      <td className={`${tdCls} font-medium`}>{personName(r)}</td>
                      <td className={tdCls}>
                        <OutcomePill outcome={r.outcome} />
                      </td>
                      <td className={tdCls} data-testid={`recipient-state-${r.user_id}`}>
                        <Pill tone={state.tone}>{state.label}</Pill>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default CampaignDetailPage;
